import { useState, useEffect } from 'react';
import {
  Search, X, Plus, Users, UserCheck, Clock, ShieldCheck, ChevronRight, Trash2, Mail,
} from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import { showAlert, showConfirm } from '@/components/AppDialog';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';

// The four REAL backend roles (auth.js validates against exactly this set).
const ROLE_ORDER = ['super_admin', 'ops', 'finance', 'support'] as const;
const ROLE_META: Record<string, { label: string; short: string; bg: string; color: string; desc: string }> = {
  super_admin: { label: 'Owner / Full access', short: 'Owner',      bg: '#F5F3FF', color: '#6D28D9', desc: 'Full access, including team management' },
  ops:         { label: 'Operations',          short: 'Operations', bg: '#EFF6FF', color: '#1D4ED8', desc: 'Clients, orders, command center & leads' },
  finance:     { label: 'Finance',             short: 'Finance',    bg: '#ECFDF5', color: '#047857', desc: 'Pricing, content, analytics & orders' },
  support:     { label: 'Support',             short: 'Support',    bg: '#FFF7ED', color: '#C2410C', desc: 'Clients, command center & notifications' },
};
const roleMeta = (r?: string) => ROLE_META[r || ''] || ROLE_META.super_admin;

function initials(name?: string, email?: string): string {
  const src = (name && name.trim()) || (email ? email.split('@')[0] : '') || '?';
  const parts = src.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

type Filter = 'all' | 'active' | 'invited';

export default function SendInvitation() {
  const [admins, setAdmins] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const [selected, setSelected] = useState<any | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('ops');
  const [submitting, setSubmitting] = useState(false);

  const authHeaders = (): Record<string, string> => {
    const token = getToken();
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/auth/admins`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.admins)) setAdmins(data.admins);
      else showAlert(data.error || 'Failed to load admins');
    } catch {
      showAlert('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleInvite = async () => {
    if (!inviteName.trim()) { showAlert('Please enter a name.'); return; }
    if (!inviteEmail.includes('@')) { showAlert('Please enter a valid work email.'); return; }
    const emailToShow = inviteEmail.trim();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/invite-admin`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: inviteName.trim(), email: emailToShow, role: inviteRole }),
      });
      const data = await res.json();
      if (data.success) {
        setInviteOpen(false);
        setInviteName('');
        setInviteEmail('');
        setInviteRole('ops');
        await fetchAdmins();
        if (data.generatedPassword) {
          showAlert(
            `Invite created for ${emailToShow}.\n\nTemporary password to share with them:\n${data.generatedPassword}`,
            { title: 'Admin invited', variant: 'success' }
          );
        } else {
          showAlert(`Invite created for ${emailToShow}. They keep their existing password.`, { title: 'Admin invited', variant: 'success' });
        }
      } else {
        showAlert(data.error || 'Failed to send invite');
      }
    } catch {
      showAlert('Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  const changeRole = async (uid: string, newRole: string) => {
    if (!uid) return;
    // Optimistically reflect the change in the open drawer.
    setSelected((s: any) => (s && s.uid === uid ? { ...s, role: newRole } : s));
    try {
      const res = await fetch(`${API_BASE_URL}/auth/admin/set-role`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ uid, role: newRole }),
      });
      const data = await res.json();
      if (data.success) fetchAdmins();
      else showAlert(data.error || 'Failed to update access level');
    } catch {
      showAlert('Failed to update access level');
    }
  };

  const handleRemove = async (admin: any) => {
    if (!admin || !admin.uid) return;
    const label = admin.name || admin.displayName || admin.email;
    const ok = await showConfirm(
      `Remove ${label} from the admin team? They lose access to the panel immediately.`,
      { destructive: true, confirmText: 'Remove' }
    );
    if (!ok) return;
    try {
      const res = await fetch(`${API_BASE_URL}/auth/remove-admin`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ uid: admin.uid }),
      });
      const data = await res.json();
      if (data.success) {
        setSelected(null);
        await fetchAdmins();
        showAlert(`${label} was removed from the admin team.`, { variant: 'success' });
      } else {
        showAlert(data.error || 'Failed to remove admin');
      }
    } catch {
      showAlert('Failed to remove admin');
    }
  };

  const total = admins.length;
  const activeCount = admins.filter((a) => a.status === 'active').length;
  const awaitingCount = admins.filter((a) => a.status === 'invited').length;

  const q = search.trim().toLowerCase();
  const filtered = admins.filter((a) => {
    if (filter === 'active' && a.status !== 'active') return false;
    if (filter === 'invited' && a.status !== 'invited') return false;
    if (!q) return true;
    const nm = (a.name || a.displayName || '').toLowerCase();
    const em = (a.email || '').toLowerCase();
    return nm.includes(q) || em.includes(q);
  });

  const drawerRoleValue = selected ? (ROLE_META[selected.role] ? selected.role : 'super_admin') : 'ops';

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <AdminSidebar />
      <div className="flex-1 xl:ml-64 adminx">
        <style>{`
          .adminx { font-family: 'Poppins', system-ui, sans-serif; color: #0F172A; padding: 32px; }
          .adminx *, .adminx *::before, .adminx *::after { box-sizing: border-box; }

          .adminx .hdr { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 24px; }
          .adminx .hdr h1 { font-size: 26px; font-weight: 700; margin: 0; letter-spacing: -0.02em; }
          .adminx .hdr .sub { margin: 6px 0 0; color: #64748B; font-size: 14px; }
          .adminx .hdr-right { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
          .adminx .owner-pill { display: inline-flex; align-items: center; gap: 6px; background: #ECFDF5; color: #047857; border: 1px solid #A7F3D0; padding: 8px 14px; border-radius: 999px; font-size: 13px; font-weight: 600; }

          .adminx .btn-primary { display: inline-flex; align-items: center; gap: 8px; background: #2563EB; color: #fff; border: none; padding: 11px 18px; border-radius: 12px; font-weight: 600; font-size: 14px; cursor: pointer; box-shadow: 0 8px 20px -8px rgba(37,99,235,.6); transition: background .15s; font-family: inherit; }
          .adminx .btn-primary:hover { background: #1D4ED8; }
          .adminx .btn-primary:disabled { opacity: .6; cursor: default; }
          .adminx .btn-ghost { border: 1.5px solid #E2E8F0; background: #fff; color: #475569; padding: 11px 18px; border-radius: 12px; font-weight: 600; font-size: 14px; cursor: pointer; font-family: inherit; }
          .adminx .btn-ghost:hover { background: #F8FAFC; }

          .adminx .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
          @media (max-width: 900px) { .adminx .stats { grid-template-columns: 1fr; } }
          .adminx .stat { background: #fff; border: 1px solid #E9EDF2; border-radius: 16px; padding: 20px; display: flex; align-items: center; gap: 16px; }
          .adminx .stat .ic { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
          .adminx .stat .lbl { font-size: 13px; color: #64748B; font-weight: 500; }
          .adminx .stat .val { font-size: 28px; font-weight: 700; margin-top: 2px; letter-spacing: -0.02em; line-height: 1; }

          .adminx .card { background: #fff; border: 1px solid #E9EDF2; border-radius: 16px; overflow: hidden; }
          .adminx .toolbar { display: flex; align-items: center; gap: 16px; padding: 18px 20px; border-bottom: 1px solid #F0F2F5; flex-wrap: wrap; }
          .adminx .search { position: relative; flex: 1; min-width: 220px; }
          .adminx .search input { width: 100%; border: 1px solid #E2E8F0; border-radius: 12px; padding: 10px 14px 10px 40px; font-size: 14px; font-family: inherit; outline: none; background: #F8FAFC; color: #0F172A; }
          .adminx .search input:focus { border-color: #2563EB; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
          .adminx .search .s-ic { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #94A3B8; pointer-events: none; }
          .adminx .chips { display: flex; gap: 8px; flex-wrap: wrap; }
          .adminx .chip { border: 1px solid #E2E8F0; background: #fff; color: #475569; padding: 8px 14px; border-radius: 999px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all .12s; }
          .adminx .chip:hover { background: #F8FAFC; }
          .adminx .chip.active { background: #2563EB; color: #fff; border-color: #2563EB; }

          .adminx .tbl-wrap { overflow-x: auto; }
          .adminx table { width: 100%; border-collapse: collapse; }
          .adminx thead th { text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: #94A3B8; font-weight: 600; padding: 14px 20px; border-bottom: 1px solid #F0F2F5; white-space: nowrap; }
          .adminx tbody td { padding: 14px 20px; border-bottom: 1px solid #F5F7FA; font-size: 14px; vertical-align: middle; }
          .adminx tbody tr { cursor: pointer; transition: background .12s; }
          .adminx tbody tr:hover { background: #F8FAFC; }
          .adminx tbody tr:last-child td { border-bottom: none; }

          .adminx .person { display: flex; align-items: center; gap: 12px; }
          .adminx .avatar { width: 40px; height: 40px; border-radius: 50%; background: #EEF2FF; color: #4338CA; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; flex-shrink: 0; }
          .adminx .avatar.lg { width: 56px; height: 56px; font-size: 18px; }
          .adminx .person .nm { font-weight: 600; color: #0F172A; }
          .adminx .person .em { font-size: 12.5px; color: #64748B; }

          .adminx .pill { display: inline-flex; align-items: center; padding: 5px 12px; border-radius: 999px; font-size: 12.5px; font-weight: 600; white-space: nowrap; }
          .adminx .pill-active { background: #ECFDF5; color: #047857; }
          .adminx .pill-invited { background: #FFFBEB; color: #B45309; }
          .adminx .view-link { display: inline-flex; align-items: center; gap: 2px; color: #2563EB; font-weight: 600; font-size: 13px; }

          .adminx .empty { text-align: center; padding: 48px 20px; color: #94A3B8; font-size: 14px; }

          /* Drawer */
          .adminx .overlay { position: fixed; inset: 0; background: rgba(15,23,42,.4); backdrop-filter: blur(2px); z-index: 1000; animation: adx-fade .18s ease-out; }
          .adminx .drawer { position: fixed; top: 0; right: 0; height: 100%; width: 420px; max-width: 92vw; background: #fff; z-index: 1001; box-shadow: -24px 0 60px -20px rgba(15,23,42,.35); display: flex; flex-direction: column; animation: adx-slide .24s cubic-bezier(.2,.8,.2,1); }
          .adminx .drawer-head { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; border-bottom: 1px solid #F0F2F5; }
          .adminx .drawer-head h3 { margin: 0; font-size: 16px; font-weight: 700; }
          .adminx .drawer-body { padding: 24px; overflow-y: auto; flex: 1; }
          .adminx .icon-btn { border: none; background: transparent; cursor: pointer; color: #94A3B8; padding: 6px; border-radius: 8px; line-height: 0; }
          .adminx .icon-btn:hover { background: #F1F5F9; color: #475569; }

          .adminx .d-person { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; }
          .adminx .d-person .nm { font-weight: 700; font-size: 17px; }
          .adminx .d-person .em { color: #64748B; font-size: 13px; margin-top: 2px; }
          .adminx label.fld { display: block; font-size: 12.5px; font-weight: 600; color: #475569; margin-bottom: 8px; }
          .adminx select.sel { width: 100%; border: 1px solid #E2E8F0; border-radius: 12px; padding: 11px 14px; font-size: 14px; font-family: inherit; background: #fff; outline: none; cursor: pointer; color: #0F172A; }
          .adminx select.sel:focus { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
          .adminx .hint { font-size: 12px; color: #94A3B8; margin: 8px 0 0; }

          .adminx .meta { margin-top: 24px; border-top: 1px solid #F0F2F5; padding-top: 8px; }
          .adminx .meta-row { display: flex; justify-content: space-between; gap: 12px; padding: 11px 0; font-size: 13.5px; border-bottom: 1px solid #F5F7FA; }
          .adminx .meta-row:last-child { border-bottom: none; }
          .adminx .meta-row .k { color: #64748B; }
          .adminx .meta-row .v { color: #0F172A; font-weight: 600; text-align: right; word-break: break-all; }
          .adminx .btn-danger { width: 100%; margin-top: 24px; display: inline-flex; align-items: center; justify-content: center; gap: 8px; border: 1px solid #FECACA; background: #FEF2F2; color: #DC2626; padding: 12px; border-radius: 12px; font-weight: 600; font-size: 14px; cursor: pointer; font-family: inherit; }
          .adminx .btn-danger:hover { background: #FEE2E2; }

          /* Modal */
          .adminx .modal-wrap { position: fixed; inset: 0; background: rgba(15,23,42,.5); backdrop-filter: blur(3px); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; animation: adx-fade .16s ease-out; }
          .adminx .modal { background: #fff; width: 100%; max-width: 520px; border-radius: 20px; box-shadow: 0 30px 70px -20px rgba(15,23,42,.5); animation: adx-pop .22s cubic-bezier(.2,.8,.2,1); max-height: 92vh; overflow-y: auto; }
          .adminx .modal-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 24px 24px 0; }
          .adminx .modal-head h2 { font-size: 20px; font-weight: 700; margin: 0; }
          .adminx .modal-head .sub { margin: 6px 0 0; color: #64748B; font-size: 13.5px; }
          .adminx .modal-body { padding: 20px 24px 24px; }
          .adminx .field { margin-bottom: 18px; }
          .adminx .field input { width: 100%; border: 1px solid #E2E8F0; border-radius: 12px; padding: 11px 14px; font-size: 14px; font-family: inherit; outline: none; color: #0F172A; }
          .adminx .field input:focus { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }

          .adminx .role-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
          @media (max-width: 520px) { .adminx .role-cards { grid-template-columns: 1fr; } }
          .adminx .role-card { border: 1.5px solid #E2E8F0; border-radius: 14px; padding: 14px; cursor: pointer; transition: all .15s; text-align: left; background: #fff; font-family: inherit; }
          .adminx .role-card:hover { border-color: #CBD5E1; }
          .adminx .role-card.sel { border-color: #2563EB; background: #EFF6FF; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
          .adminx .role-card .rc-name { font-weight: 600; font-size: 14px; color: #0F172A; }
          .adminx .role-card .rc-desc { font-size: 12px; color: #64748B; margin-top: 3px; line-height: 1.4; }
          .adminx .modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 22px; }

          @keyframes adx-slide { from { transform: translateX(100%); } to { transform: none; } }
          @keyframes adx-fade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes adx-pop { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: none; } }
          @media (prefers-reduced-motion: reduce) {
            .adminx .drawer, .adminx .modal, .adminx .overlay, .adminx .modal-wrap { animation: none !important; }
          }
        `}</style>

        {/* Header */}
        <div className="hdr">
          <div>
            <h1>Admins</h1>
            <p className="sub">Manage who can access the admin panel and what each person is allowed to do.</p>
          </div>
          <div className="hdr-right">
            <span className="owner-pill"><ShieldCheck className="w-4 h-4" /> You are the owner</span>
            <button className="btn-primary" onClick={() => setInviteOpen(true)}>
              <Plus className="w-4 h-4" /> Invite admin
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="stats">
          <div className="stat">
            <div className="ic" style={{ background: '#EEF2FF' }}><Users className="w-5 h-5" style={{ color: '#4338CA' }} /></div>
            <div>
              <div className="lbl">People with access</div>
              <div className="val">{total}</div>
            </div>
          </div>
          <div className="stat">
            <div className="ic" style={{ background: '#ECFDF5' }}><UserCheck className="w-5 h-5" style={{ color: '#047857' }} /></div>
            <div>
              <div className="lbl">Active</div>
              <div className="val">{activeCount}</div>
            </div>
          </div>
          <div className="stat">
            <div className="ic" style={{ background: '#FFFBEB' }}><Clock className="w-5 h-5" style={{ color: '#B45309' }} /></div>
            <div>
              <div className="lbl">Awaiting setup</div>
              <div className="val">{awaitingCount}</div>
            </div>
          </div>
        </div>

        {/* Search + chips + table */}
        <div className="card">
          <div className="toolbar">
            <div className="search">
              <Search className="s-ic w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="chips">
              <button className={`chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
              <button className={`chip ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>Active</button>
              <button className={`chip ${filter === 'invited' ? 'active' : ''}`} onClick={() => setFilter('invited')}>Awaiting setup</button>
            </div>
          </div>

          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Access level</th>
                  <th>Added</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5}><div className="empty">Loading admins…</div></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5}><div className="empty">{admins.length === 0 ? 'No admins yet. Invite your first teammate.' : 'No admins match your search.'}</div></td></tr>
                ) : (
                  filtered.map((admin, i) => {
                    const rm = roleMeta(admin.role);
                    const nm = admin.name || admin.displayName || 'Pending Invite';
                    return (
                      <tr key={admin.uid || i} onClick={() => setSelected(admin)}>
                        <td>
                          <div className="person">
                            <div className="avatar">{initials(admin.name || admin.displayName, admin.email)}</div>
                            <div>
                              <div className="nm">{nm}</div>
                              <div className="em">{admin.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="pill" style={{ background: rm.bg, color: rm.color }}>{rm.label}</span>
                        </td>
                        <td style={{ color: '#64748B' }}>
                          {admin.addedAt ? new Date(admin.addedAt).toLocaleDateString() : '—'}
                        </td>
                        <td>
                          {admin.status === 'invited' ? (
                            <span className="pill pill-invited">Awaiting setup</span>
                          ) : (
                            <span className="pill pill-active">Active</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span className="view-link">View <ChevronRight className="w-4 h-4" /></span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="adminx" style={{ padding: 0 }}>
          <div className="overlay" onClick={() => setSelected(null)} />
          <div className="drawer" role="dialog" aria-modal="true" aria-label="Admin details">
            <div className="drawer-head">
              <h3>Admin details</h3>
              <button className="icon-btn" aria-label="Close" onClick={() => setSelected(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="drawer-body">
              <div className="d-person">
                <div className="avatar lg">{initials(selected.name || selected.displayName, selected.email)}</div>
                <div>
                  <div className="nm">{selected.name || selected.displayName || 'Pending Invite'}</div>
                  <div className="em">{selected.email}</div>
                </div>
              </div>

              <label className="fld">Access level</label>
              <select
                className="sel"
                value={drawerRoleValue}
                onChange={(e) => changeRole(selected.uid, e.target.value)}
              >
                {ROLE_ORDER.map((r) => (
                  <option key={r} value={r}>{ROLE_META[r].label}</option>
                ))}
              </select>
              <p className="hint">{roleMeta(drawerRoleValue).desc}</p>

              <div className="meta">
                <div className="meta-row">
                  <span className="k">Status</span>
                  <span className="v">{selected.status === 'invited' ? 'Awaiting setup' : 'Active'}</span>
                </div>
                <div className="meta-row">
                  <span className="k">Added</span>
                  <span className="v">{selected.addedAt ? new Date(selected.addedAt).toLocaleString() : '—'}</span>
                </div>
                <div className="meta-row">
                  <span className="k">User ID</span>
                  <span className="v">{selected.uid || '—'}</span>
                </div>
              </div>

              <button className="btn-danger" onClick={() => handleRemove(selected)}>
                <Trash2 className="w-4 h-4" /> Remove from admin team
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {inviteOpen && (
        <div className="adminx" style={{ padding: 0 }}>
        <div className="modal-wrap" onMouseDown={(e) => { if (e.target === e.currentTarget) setInviteOpen(false); }}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="Invite admin">
            <div className="modal-head">
              <div>
                <h2>Invite an admin</h2>
                <p className="sub">They get a temporary password to sign in and set up their account.</p>
              </div>
              <button className="icon-btn" aria-label="Close" onClick={() => setInviteOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="modal-body">
              <div className="field">
                <label className="fld">Name</label>
                <input
                  type="text"
                  placeholder="Jane Doe"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
              </div>
              <div className="field">
                <label className="fld">Work email</label>
                <input
                  type="email"
                  placeholder="jane@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <label className="fld">Access level</label>
                <div className="role-cards">
                  {ROLE_ORDER.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={`role-card ${inviteRole === r ? 'sel' : ''}`}
                      onClick={() => setInviteRole(r)}
                    >
                      <div className="rc-name">{ROLE_META[r].label}</div>
                      <div className="rc-desc">{ROLE_META[r].desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-ghost" onClick={() => setInviteOpen(false)} disabled={submitting}>Cancel</button>
                <button className="btn-primary" onClick={handleInvite} disabled={submitting}>
                  <Mail className="w-4 h-4" /> {submitting ? 'Sending…' : 'Create & send invite'}
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
}
